import React from "react";
import { URLS } from "Utils/apiURL";
import { ENRICH_CONTACT_FIELDS } from "Pages/Dashboard/data-toolkit/constants";
import StatusPill from "Pages/Dashboard/data-toolkit/StatusPill";
import ToolRunner from "Pages/Dashboard/data-toolkit/ToolRunner";

const cellHas = (row, idx) =>
  idx != null && ((row && row[idx]) || "").trim() !== "";

// A row is enrichable with a LinkedIn URL, or a name (full, or first + last)
// together with a company (domain or name).
const rowUsable = (row, m) => {
  if (cellHas(row, m.linkedinUrl)) return true;
  const hasName =
    cellHas(row, m.fullName) ||
    (cellHas(row, m.firstName) && cellHas(row, m.lastName));
  const hasCompany = cellHas(row, m.companyDomain) || cellHas(row, m.companyName);
  return hasName && hasCompany;
};

const hasEnrichableColumns = mapping => {
  if (mapping.linkedinUrl != null) return true;
  const hasName =
    mapping.fullName != null ||
    (mapping.firstName != null && mapping.lastName != null);
  const hasCompany =
    mapping.companyDomain != null || mapping.companyName != null;
  return hasName && hasCompany;
};

const fieldVal = (r, key) => (r.fields && r.fields[key]) || "";

// Text cell, width-limited with ellipsis + hover tooltip (keeps long values like
// company description from stretching the table).
const textCol = (header, key) => ({
  header,
  cell: r => {
    const v = fieldVal(r, key);
    return v ? (
      <span className="dtTrunc" title={v}>
        {v}
      </span>
    ) : (
      "-"
    );
  }
});

// Link cell (LinkedIn, socials, company website) — compact "View" link.
const linkCol = (header, key) => ({
  header,
  cell: r =>
    fieldVal(r, key) ? (
      <a href={fieldVal(r, key)} target="_blank" rel="noopener noreferrer">
        View
      </a>
    ) : (
      "-"
    )
});

const ENRICH_STATS = (counts, job) => [
  { label: "Enriched", value: counts.found || 0 },
  { label: "Not found", value: counts.notFound || 0 },
  ...(counts.skipped ? [{ label: "Skipped", value: counts.skipped }] : []),
  { label: "Credits used", value: (job && job.creditsCharged) || 0, brand: true }
];

// Full field set, matching the CSV. Long cells are width-limited (see .dtTrunc);
// the CSV download carries the untruncated values.
const ENRICH_COLUMNS = [
  { header: "Status", cell: r => <StatusPill status={r.emailStatus} /> },
  // How the person was matched (Exact / Company match / Name only …), or the
  // candidates seen when no match could be confirmed.
  textCol("Match", "match"),
  textCol("First name", "firstName"),
  textCol("Last name", "lastName"),
  textCol("Title", "title"),
  textCol("Headline", "headline"),
  textCol("Seniority", "seniority"),
  textCol("Department", "department"),
  textCol("Person industry", "personIndustry"),
  textCol("Location", "location"),
  textCol("City", "city"),
  textCol("State", "state"),
  textCol("Country", "country"),
  linkCol("LinkedIn", "linkedin"),
  linkCol("Twitter", "twitter"),
  linkCol("Facebook", "facebook"),
  linkCol("GitHub", "github"),
  {
    header: "Email",
    cell: r =>
      r.fields && r.fields.email ? (
        <a className="dtTrunc" title={r.fields.email} href={`mailto:${r.fields.email}`}>
          {r.fields.email}
        </a>
      ) : (
        "-"
      )
  },
  textCol("Followers", "followers"),
  textCol("Connections", "connections"),
  textCol("Skills", "skills"),
  textCol("Languages", "languages"),
  textCol("Education", "education"),
  textCol("Awards", "awards"),
  textCol("Organizations", "organizations"),
  textCol("Company", "company"),
  textCol("Company domain", "companyDomain"),
  linkCol("Company website", "companyWebsite"),
  linkCol("Company LinkedIn", "companyLinkedin"),
  textCol("Industry", "companyIndustry"),
  textCol("Company size", "companySize"),
  textCol("Revenue", "companyRevenue"),
  textCol("Company type", "companyType"),
  textCol("Company location", "companyLocation"),
  textCol("Founded", "companyFounded"),
  textCol("Company description", "companyDescription")
];

const CONFIG = {
  toolKey: "ENRICH_CONTACT",
  iconKey: "enrich-contacts",
  title: "Enrich contacts",
  description:
    "Look up a contact by LinkedIn URL, or name and company, then fill in their job title, seniority, company and location. 1 credit per contact we can enrich; no match is free.",
  fields: ENRICH_CONTACT_FIELDS,
  endpoint: URLS.dataToolkitEnrichContact,
  runNoun: "Enrich contacts run",
  runLabel: "Enrich contacts",
  foundLabel: "Enriched",
  inputSub:
    "Paste rows or upload a CSV. Identify each contact by a LinkedIn URL, or a name and company (domain or name).",
  pastePlaceholder:
    "name, company, linkedin url\nElon Musk, tesla.com, https://linkedin.com/in/elonmusk",
  mappingHint: (
    <>
      Tell us how to find each contact: <b>a LinkedIn URL</b>, or <b>a name</b>
      (full name, or first and last) with <b>a company</b> (domain or name).
    </>
  ),
  requiredCheck: hasEnrichableColumns,
  requiredError:
    "Map a LinkedIn URL, or a name (full name, or first and last) plus a company (domain or name).",
  rowCredits: (row, m) => (rowUsable(row, m) ? 1 : 0),
  costNote: max => (
    <>
      This run will use up to <b>{max.toLocaleString()}</b> credit
      {max === 1 ? "" : "s"}. You're only charged for contacts we can enrich; no
      match is free.
    </>
  ),
  stats: ENRICH_STATS,
  columns: ENRICH_COLUMNS
};

const EnrichContacts = () => <ToolRunner cfg={CONFIG} />;

export default EnrichContacts;
